import {
  ACCOUNT_TYPE_DEFINITIONS,
  PRIMARY_USE_DEFINITIONS,
  PROJECT_ACCOUNT_ROLE_DEFINITIONS,
  ROLE_FAMILY_DEFINITIONS,
} from './entity-vocabularies.js';
import { PROJECT_STAGE_DEFINITIONS } from './project-stages.js';
import { DISPOSITION_DEFINITIONS } from './signal-type-registry.js';
import { SYSTEM_SKILL_WORKFLOW_VERSIONS } from './skills.js';
import type { SystemSkillDefinition } from './system-skills.js';

/** The fields this workflow names, by score, so a test can hold them to the real field list. */
export const SET_UP_SCORE_FIELDS = {
  icp: ['type', 'project.city', 'project.use', 'project.stage', 'project.units', 'signal.filed'],
  atlas: [
    'city',
    'use',
    'stage',
    'units',
    'sqft',
    'cost',
    'status',
    'firm.role',
    'firm.score',
    'last_signal',
  ],
  prospect: ['persona', 'firm.score', 'has_email'],
} as const;

/** The values this workflow lists, by dataset and field, so a test can hold them to what each field takes. */
export const SET_UP_SCORE_VALUES = {
  account: {
    type: ACCOUNT_TYPE_DEFINITIONS,
    'project.use': PRIMARY_USE_DEFINITIONS,
    'project.stage': PROJECT_STAGE_DEFINITIONS,
  },
  project: {
    use: PRIMARY_USE_DEFINITIONS,
    stage: PROJECT_STAGE_DEFINITIONS,
    status: DISPOSITION_DEFINITIONS,
    'firm.role': PROJECT_ACCOUNT_ROLE_DEFINITIONS,
  },
  contact: { persona: ROLE_FAMILY_DEFINITIONS },
} as const;

const fieldList = (key: keyof typeof SET_UP_SCORE_FIELDS) => SET_UP_SCORE_FIELDS[key].join(', ');
const values = (definitions: readonly { value: string; label: string }[]) =>
  definitions.map((d) => `${d.label} = ${d.value}`).join('; ');

const INSTRUCTIONS = [
  '# Setting up a score',

  "The member opened this chat from a scoring page to set up one of the workspace's scores, and their first message names it. Start from what the workspace already says about who they sell to, ask only for what is missing, and put criteria on an approval card, quickly: they are waiting and watching.",

  '## The flow',

  [
    '1. First step: call workspace_scores with action list. In that same step, if the Knowledge index lists a page on who the firm sells to, its ideal customer, its market or its positioning, call get_knowledge on it, two pages at most. If the index says pages were left out and none listed fits, call find_knowledge once instead. Read nothing else: no query_data, get_fields, View searches or workspace_scores preview (the card counts matches itself), and no report on their records.',
    "2. Next step: work out the answers this score needs (below). The member and their knowledge settle an answer. A score's current criteria only suggest one, since the member may be here to change them. If every answer is settled, go to step 3. If not, call ask_member once with only the unsettled ones, after one short line saying what you already found and on which page. Never ask what knowledge already answers.",
    '3. Next step: call workspace_manage_scores with action update. Keep any words before it to one short line, naming the knowledge page the criteria came from. The card is your proposal: it lists each criterion in words with its match count, and the tiers before and after. Never ask whether to save: approving the card is the answer.',
    '4. Once it has run, say in one or two sentences what was saved and how the tiers moved, then offer: "Want me to check this against your accounts?" Look at their records only if they say yes.',
    'If they ask for something else, such as advice or a look at their accounts, do that instead.',
  ].join('\n'),

  '## The answers each score needs',

  [
    '- ICP Score: who they sell to, their markets, and the project types they work on.',
    '- Atlas Score: their markets, project types and project stages. A size floor only when knowledge or the member names one.',
    '- Prospect Score: who they sell to.',
    "- These overlap, so read one score's criteria for another: ICP firm types and Atlas cities are the same answers.",
  ].join('\n'),

  '## Asking',

  [
    '- One ask_member call, at most 4 questions, each with 2 to 4 options. An option is picked alone, so make each one a whole answer, such as "Developers and architects". The member can also type their own.',
    '- Build the options from what you read, and set recommended to the one knowledge or the current criteria best support. With neither, recommend a common choice and say it is only a starting point.',
  ].join('\n'),

  '## Writing the criteria',

  [
    "- A criterion is {id, label?, impact, where}, and where is {combinator: 'and', rules: [{field, operator, value}]}. An id is 1 to 40 lowercase letters, digits or underscores.",
    '- Pass bucket_labels exactly as the read returned them. Rename a tier only when the member asks.',
    '- update replaces the whole list. Keep, unchanged and with the same id, every existing criterion their answers do not touch, such as a recent-signal rule or a disqualifier. When they say only, just or start over, drop the rest.',
    '- Use one criterion for each thing they named, with all its values in one rule. Reuse the id of an existing criterion that reads the same thing.',
    '- Who they sell to is very_important; markets, project types, stages and size are important, unless they say otherwise. Use disqualify only when they ask to rule something out.',
    '- On the Prospect Score, who they sell to names firm types: write it as the personas who buy at those firms.',
    '- Rules on one relation in one group must match the same record: a firm with a Boston project that is also multifamily is one group.',
  ].join('\n'),

  '## Fields, so there is nothing to look up',

  [
    `- ICP Score (key icp, accounts): ${fieldList('icp')}.`,
    `- Atlas Score (key atlas, projects): ${fieldList('atlas')}.`,
    `- Prospect Score (key prospect, contacts): ${fieldList('prospect')}.`,
    "- Lists take operator in with an array of values. Sizes (units, sqft, cost, project.units) take > with a number. Dates (signal.filed, last_signal) take withinLast with {amount, unit}, where unit is days or months. firm.score takes between with [75, 100] for the top tier. has_email takes = with true. A firm on a project's team pairs firm.role with firm.score in one group.",
    `- Firm types (type): ${values(ACCOUNT_TYPE_DEFINITIONS)}.`,
    `- Roles on a project (firm.role): ${values(PROJECT_ACCOUNT_ROLE_DEFINITIONS)}.`,
    `- Project types (use, project.use): ${values(PRIMARY_USE_DEFINITIONS)}.`,
    `- Stages (stage, project.stage): ${values(PROJECT_STAGE_DEFINITIONS)}.`,
    `- Project status (status): ${values(DISPOSITION_DEFINITIONS)}.`,
    `- Personas (persona): ${values(ROLE_FAMILY_DEFINITIONS)}.`,
    '- Cities are written as they read, such as Boston.',
  ].join('\n'),

  '## What is normal, so do not raise it',

  [
    '- A score ranks every record the workspace can see in Parcel, not only the ones it has saved. A total far above the saved accounts is correct.',
    '- Tiers are score bands: 75 and up, 50 and up, 25 and up, and everything below 25 in the last tier, with anything a Disqualify criterion hit. Records there are usually low scorers, not disqualified ones.',
    '- Adding a criterion raises the maximum score, so records that miss it can drop a tier.',
    "- is_template only marks the three built-in scores, whose names are fixed. Their criteria are the workspace's own.",
    '- If one criterion matches nearly every record, say so in your closing sentence, since it separates almost no one.',
  ].join('\n'),
].join('\n\n');

export const SET_UP_SCORE_V1: SystemSkillDefinition = {
  key: 'set_up_score',
  version: SYSTEM_SKILL_WORKFLOW_VERSIONS.set_up_score,
  reservedSlug: 'set-up-score',
  name: 'Set up a score',
  description:
    "Reads the workspace's knowledge for who the firm sells to, asks only for what is missing, and puts a score's criteria on an approval card.",
  allowedSkillTools: [],
  instructions: INSTRUCTIONS,
};
